import { useIntl } from "react-intl";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { useSwitchURLToTripId } from "src/domain/TripPlanner/hooks/useSwitchURLToTripId";
import { useLogout } from "src/auth/queries";
import { useScreenMinWidth } from "src/utils/hooks/useScreenMinWidth";
import { Button } from "src/design-system/components/Button/Button";
import { type UserProfile, getUserEmail } from "../../utils/user";
import { ManageProfileDialog } from "../ManageProfile/ManageProfileDialog/ManageProfileDialog";
import { ManageProfileButton } from "../ManageProfile/ManageProfileScreen/ManageProfileScreen";
import { messages } from "./UserDetails.messages";

type UserProfileProps = {
  user: UserProfile;
};

export function UserDetails({ user }: UserProfileProps) {
  const { isMobile } = useScreenMinWidth();
  const intl = useIntl();
  const { clearTripId } = useSwitchURLToTripId();
  const logout = useLogout();

  function handleSignOutClick() {
    sendAnalyticsInteractionEvent({
      category: "UserAccount",
      action: "Click:SignOut",
    });
    logout.mutate(undefined, {
      onSuccess() {
        clearTripId();
        window.location.reload();
      },
    });
  }

  const mainEmail = getUserEmail(user);

  return (
    <div className="flex flex-col items-center lg:items-start">
      <div className="mb-6 h-18 w-18 overflow-hidden rounded-full lg:h-14 lg:w-14">
        <DefaultPicture name={user.name} />
      </div>
      <div className="min-h-4 max-w-59 truncate pb-2 text-heading-xl font-semibold lg:mb-1 lg:text-heading-md">
        {user.name}
      </div>
      <p className="min-h-4 max-w-59 truncate pb-6 text-body-md font-normal text-text-primary lg:text-body-lg lg:font-light">
        {mainEmail}
      </p>
      <div className="mb-6 w-full text-center lg:mb-4 lg:h-12 lg:border-b lg:border-border-secondary lg:text-start">
        {isMobile ? (
          <ManageProfileButton />
        ) : (
          <ManageProfileDialog user={user} />
        )}
      </div>
      <div className="flex w-full flex-col items-center px-4 py-2">
        <Button size="small" variant="subtle" onPress={handleSignOutClick}>
          {intl.formatMessage(messages.signOut)}
        </Button>
      </div>
    </div>
  );
}

function DefaultPicture(props: { name: string }) {
  const firstLetter = props.name?.trim().charAt(0).toUpperCase() ?? "";
  return (
    <div className="flex h-full w-full items-center justify-center bg-neutral-1600 text-2xl font-semibold text-fill-solid">
      {firstLetter}
    </div>
  );
}
